import { useEffect, useState } from "react";
import { useTranslation } from "./i18n";

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export default function PwaInstallButton() {
  const { t } = useTranslation("pwaInstall");
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [installed, setInstalled] = useState(() => window.matchMedia("(display-mode: standalone)").matches);
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !installed;

  useEffect(() => {
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPrompt);
    };
    const complete = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", complete);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", complete);
    };
  }, []);

  if (installed) return <p className="pwa-installed-badge">{t("installed")}</p>;
  if (prompt)
    return (
      <button
        type="button"
        className="pwa-install-button"
        onClick={() =>
          void (async () => {
            await prompt.prompt();
            const choice = await prompt.userChoice;
            if (choice.outcome === "accepted") setInstalled(true);
            setPrompt(null);
          })()
        }
      >
        {t("install")}
      </button>
    );
  if (isiOS) return <p className="pwa-install-help">{t("iosHelp")}</p>;
  return <p className="pwa-install-help">{t("browserHelp")}</p>;
}
